import { synthesizeSpeech } from "../methods";
import { PollyAudioContext, splitTextIntoPollyChunks } from "../utils";

import {
  SpeechMark,
  AudioContext,
  ReadOutLoudArgs,
  ReadOutLoudCallbacks,
} from "./typings";
import {
  getNewWordNode,
  getTextFromRange,
  removeHighlightNode,
  stopAllAudioPlayers,
} from "./utils";

const PRELOAD_NEXT_CHUNK_WHEN_MS_LEFT = 30_000;
const MIN_PRELOAD_WAIT_MS = 500;

/**
 * Singleton that allows for speaking a given range out loud, inclucding karaoke.
 *
 * Only one instance of this should exists as it also functions as the single
 * source of truth of what is currently being read out loud on the host
 */
export class Speaker {
  private static currentId = 0;

  private static args?: ReadOutLoudArgs;

  /** Contains the currently active callbacks from the most recent Speaker call */
  private static callbacks?: ReadOutLoudCallbacks;

  /** References the audio-player used to read out loud */
  private static audioPlayer: PollyAudioContext;

  /**
   * If a reading-session is currently active, then this will contain karaoke-
   * metadata.
   */
  private static speechMarks?: SpeechMark[];

  private static progressTimeout?: number;
  private static prevProgress = 0;

  private static chunks?: string[];
  private static loadedChunks: 0;
  private static loadNextChunkTimeout?: number;

  /** Fields related to karaoke */
  private static range: Range;
  private static remainingRange?: Range;
  private static currentWordIndex = -1;
  private static prevHighlightNode?: HTMLSpanElement;
  private static karaokeTimeout?: number;

  /**
   * Initializes the Speaker to read a new URL out loud and begins reading as
   * soon as data has been loaded.
   */
  public static readOutLoud(args: ReadOutLoudArgs): AudioContext {
    // Instantiate our audio-player the first time the user starts reading out loud.
    // (We assume that this is triggered by a touch/click event, since this will
    // allow the audioContext to enter and keep its 'running' state).
    if (!Speaker.audioPlayer) {
      Speaker.audioPlayer = new PollyAudioContext({ onEnd: Speaker.onEnd });
    }

    // Ensure previous readings are cancelled
    Speaker.reset(true);
    const id = ++Speaker.currentId;
    Speaker.range = args.range;
    Speaker.args = args;
    Speaker.remainingRange = args.range.cloneRange();

    // Bind the currently passed callback to only work on the current instance
    // of the Speaker. I.e. in case new audio is started, then the previously
    // inserted callbacks will cease to function
    Speaker.callbacks = {
      onCancel: Speaker.onlyWhenMatchingId(id, args.onCancel),
      onEnd: Speaker.onlyWhenMatchingId(id, args.onEnd),
      onKaraokeHighlight: Speaker.onlyWhenMatchingId(
        id,
        args.onKaraokeHighlight
      ),
      onKaraokeNodeHide: Speaker.onlyWhenMatchingId(id, args.onKaraokeNodeHide),
      onProgress: Speaker.onlyWhenMatchingId(id, args.onProgress),
      onReady: Speaker.onlyWhenMatchingId(id, args.onReady),
      onStart: Speaker.onlyWhenMatchingId(id, args.onStart),
    };

    const audioContext = {
      pause: Speaker.onlyWhenMatchingId(id, Speaker.pauseAudio),
      resume: Speaker.onlyWhenMatchingId(id, Speaker.resumeAudio),
      stop: Speaker.onlyWhenMatchingId(id, () => {
        Speaker.onEnd(true);
      }),
    };

    const input = getTextFromRange(args.range)
      .replace(/\r|\t/g, "\n")
      .replace(/\s+\n/g, "\n")
      .replace(/([^.:!?])\n+/g, "$1. ")
      .replace(/\s+/g, " ")
      .trim();

    // simple considered empty ranges to be read out loud right away
    if (input.length === 0) {
      args.onEnd?.();

      return audioContext;
    }

    Speaker.chunks = splitTextIntoPollyChunks(input);
    Speaker.loadedChunks = 0;

    synthesizeSpeech({
      text: Speaker.chunks[0],
      includeSpeechMarks: Speaker.args.karaoke,
      speed: args.speed,
      voice: args.voice,
      cacheForMs: args.cacheForMs,
    }).then(
      async ({ mp3Url, speechMarks }) => {
        // In case the currently playing Speaker has changed since the request
        // began then bail out.
        if (id !== Speaker.currentId) {
          args.onEnd?.();

          return;
        }

        try {
          // Once data has been loaded, then stop all audio elements (including
          // host audio elements) in preparation of starting our new Speaker
          stopAllAudioPlayers();

          Speaker.speechMarks = speechMarks;

          // ... and then begin a new audioPlayer
          await Speaker.audioPlayer?.addUrl(mp3Url);

          if (Speaker.callbacks?.onReady) {
            await Speaker.callbacks.onReady(async () =>
              Speaker.audioPlayer.play()
            );
          } else {
            await Speaker.audioPlayer.play();
          }

          Speaker.loadedChunks++;

          if (Speaker.args?.karaoke && Speaker.speechMarks?.length) {
            Speaker.karaoke();
          }

          // trigger the start callback now that we've begun playback
          if (Speaker.callbacks?.onStart) {
            Speaker.callbacks.onStart();
          }

          // There's no need to track progress if the host doesn't act on it..
          if (Speaker.callbacks?.onProgress) {
            Speaker.trackProgress();
          }

          // Load next chunk immediately!
          Speaker.loadNextChunkTimeout = window.setTimeout(() => {
            Speaker.loadNextChunk();
          }, 1000);
        } catch (err) {
          args.onError?.(<unknown>err);
        }
      },
      (reason: unknown) => {
        args.onError?.(reason);
      }
    );

    // Return an audio-context external elements can use to keep track of the
    // audio-player. This context will only be valid as long as no new Speakings
    // are started.
    return audioContext;
  }

  private static loadNextChunk() {
    if (!this.chunks || Speaker.loadedChunks >= this.chunks.length) {
      return;
    }

    const wait = Math.max(
      MIN_PRELOAD_WAIT_MS,
      Speaker.audioPlayer.duration -
        Speaker.audioPlayer.currentTime -
        PRELOAD_NEXT_CHUNK_WHEN_MS_LEFT
    );

    this.loadNextChunkTimeout = window.setTimeout(() => {
      if (!this.chunks) {
        return;
      }

      synthesizeSpeech({
        text: this.chunks[Speaker.loadedChunks],
        includeSpeechMarks: Speaker.args?.karaoke,
        speed: Speaker.args?.speed,
        voice: Speaker.args?.voice,
        cacheForMs: Speaker.args?.cacheForMs,
      }).then(async ({ mp3Url, speechMarks }) => {
        const prevDuration = Speaker.audioPlayer.duration;
        const mappedSpeechMarks = mapSpeechMarks(speechMarks, prevDuration);
        Speaker.speechMarks = [
          ...(Speaker.speechMarks ?? []),
          ...(mappedSpeechMarks ?? []),
        ];

        await Speaker.audioPlayer.addUrl(mp3Url);
        Speaker.loadedChunks++;
        Speaker.loadNextChunk();
      });
    }, wait);
  }

  /**
   * Start karaoke, which highlights the word that is currently spoken by the
   * speech synthesis
   */
  private static karaoke() {
    window.clearTimeout(Speaker.karaokeTimeout);

    const onTick = () => {
      if (
        !Speaker.audioPlayer ||
        !Speaker.speechMarks ||
        !Speaker.remainingRange
      ) {
        return;
      }

      const res = getNewWordNode({
        prevIndex: Speaker.currentWordIndex,
        progressInMs: Speaker.audioPlayer.currentTime,
        range: Speaker.remainingRange,
        speechMarks: Speaker.speechMarks,
      });

      // In case a new word has been extracted, then ensure we remove previous
      // nodes and update our metadata
      if (res) {
        Speaker.currentWordIndex = res.newWordIndex;
        removeHighlightNode(
          Speaker.prevHighlightNode,
          Speaker.callbacks?.onKaraokeNodeHide
        ).catch((err) => {
          console.error("Failed to hide karaoke node", err);
        });

        Speaker.callbacks?.onKaraokeHighlight?.(res.highlightNode);
        Speaker.prevHighlightNode = res.highlightNode;
      }

      Speaker.karaokeTimeout = requestAnimationFrame(onTick);
    };

    Speaker.karaokeTimeout = requestAnimationFrame(onTick);
  }

  private static onSeeked() {
    // Ensure karaoke remaining range is reset once progress is altered, since we
    // will need to original range since we do not know where the user decided to
    // seek to.
    if (Speaker.args?.karaoke) {
      Speaker.resetKaraoke();
      Speaker.remainingRange = Speaker.range.cloneRange();
      Speaker.karaoke();
    }
  }

  private static trackProgress() {
    if (Speaker.progressTimeout) {
      window.cancelAnimationFrame(Speaker.progressTimeout);
    }

    const onProgress = () => {
      const estimatedTotalDuration =
        (Speaker.audioPlayer.duration / Speaker.audioPlayer.loadedBuffers) *
        (this.chunks?.length ?? 1);
      const nextProgress = Math.max(
        this.prevProgress,
        Speaker.audioPlayer.currentTime / estimatedTotalDuration
      );

      Speaker.callbacks?.onProgress?.(nextProgress);

      this.prevProgress = nextProgress;
      Speaker.progressTimeout = window.requestAnimationFrame(onProgress);
    };

    Speaker.progressTimeout = window.requestAnimationFrame(onProgress);
  }

  /**
   * Method returned to the host, allowing to resume paused speakers.
   */
  private static async resumeAudio() {
    if (!Speaker.audioPlayer.paused) {
      return;
    }

    await Speaker.audioPlayer.resume();
    Speaker.loadNextChunk();

    if (Speaker.args?.karaoke) {
      Speaker.karaoke();
    }

    if (Speaker.callbacks?.onProgress) {
      Speaker.trackProgress();
    }
  }

  /**
   * Method returned to the host, allowing pausing and playing of the Speaker
   */
  private static pauseAudio() {
    window.clearTimeout(Speaker.loadNextChunkTimeout);

    if (Speaker.karaokeTimeout) {
      window.cancelAnimationFrame(Speaker.karaokeTimeout);
      removeHighlightNode(
        Speaker.prevHighlightNode,
        Speaker.callbacks?.onKaraokeNodeHide
      ).catch((err) => {
        console.error("Failed to hide karaoke node", err);
      });
      Speaker.prevHighlightNode = undefined;
    }

    if (Speaker.progressTimeout) {
      window.cancelAnimationFrame(Speaker.progressTimeout);
    }

    Speaker.audioPlayer.pause();
  }

  /**
   * Method returned to the host, completely stopping the speaker.
   *
   * NB: If this method is called, then calling resumeAudio() from the host
   * will not work.
   */
  private static onEnd(cancelled?: boolean) {
    // In case the playback was cancelled due to whatever reason, then reset
    // immediately.
    if (cancelled) {
      Speaker.reset(cancelled);

      return;
    }

    // Resets should not occur before all chunks have been played..
    if (
      Speaker.loadedChunks < (Speaker.chunks?.length ?? 0) ||
      Speaker.audioPlayer.currentTime < Speaker.audioPlayer.duration
    ) {
      return;
    }

    Speaker.reset(cancelled);
  }

  private static reset(cancelled?: boolean) {
    if (Speaker.progressTimeout) {
      window.cancelAnimationFrame(Speaker.progressTimeout);
    }

    window.clearTimeout(Speaker.loadNextChunkTimeout);
    Speaker.resetKaraoke();
    Speaker.speechMarks = undefined;
    Speaker.args = undefined;
    Speaker.prevProgress = 0;

    Speaker.audioPlayer.pause();
    Speaker.audioPlayer.reset();

    if (cancelled) {
      Speaker.callbacks?.onCancel?.();
    } else {
      Speaker.callbacks?.onEnd?.();
    }

    // remove speaker callbacks, so we no longer trigger any legacy callbacks
    Speaker.callbacks = undefined;
  }

  private static resetKaraoke() {
    Speaker.remainingRange = undefined;
    Speaker.currentWordIndex = -1;
    removeHighlightNode(
      Speaker.prevHighlightNode,
      Speaker.callbacks?.onKaraokeNodeHide
    ).catch((err) => {
      console.error("Failed to hide karaoke node", err);
    });
    Speaker.prevHighlightNode = undefined;

    if (Speaker.karaokeTimeout) {
      window.cancelAnimationFrame(Speaker.karaokeTimeout);
    }
  }

  /**
   * Safetynet to ensure that callback functions from the host only can be executed
   * as long as the id passed when they were bound matches the current id of the Speaker.
   */
  private static onlyWhenMatchingId(id: number, cb: undefined): undefined;
  private static onlyWhenMatchingId<A extends unknown[], R>(
    id: number,
    cb: (...args: A) => R
  ): (...args: A) => R;
  private static onlyWhenMatchingId<A extends unknown[], R>(
    id: number,
    cb: ((...args: A) => R) | undefined
  ): ((...args: A) => R) | undefined;
  private static onlyWhenMatchingId<A extends unknown[], R>(
    id: number,
    cb: ((...args: A) => R) | undefined
  ): ((...args: A) => R) | undefined {
    if (!cb) {
      return;
    }

    return (...args: A): R => {
      if (id !== Speaker.currentId) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: hack'ish, but we only trigger callback if matching ID - and
        // no one will care about void return values if this is no longer the
        // latest test
        return;
      }

      return cb(...args);
    };
  }
}

/**
 * Maps the time of speechmarks in a chunk to take other chunks into account
 */
function mapSpeechMarks(
  speechMarks: { time: number; value: string }[] | undefined,
  currDuration: number
) {
  return speechMarks?.map((speechMark) => ({
    time: speechMark.time + currDuration,
    value: speechMark.value,
  }));
}
